import { CalendarDate, parseDate } from '@internationalized/date';
import { castArray, compact } from 'lodash/fp';
import { DateTime } from 'luxon';
import { ChangeEvent, FC } from 'react';
import styled from 'styled-components';

import ReactSelect, { OptionType, ReactCreatableSelect } from 'dashboard/components/ReactSelect';
import reactSelectStyles from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/react-select-styles';
import { useWorkflowActionMeta } from 'dashboard/components/WorkflowEditor/WorkflowActionMetaProvider/WorkflowActionMetaProvider';
import { generateListOptions, getJsonSchemaType, JsonLogicValue } from 'dashboard/lib/workflows';
import { generateConditionalOptions } from 'dashboard/lib/workflows/conditional';
import { ListStatus } from 'dashboard/models/list';
import {
  AGE_OPERATORS,
  ARRAY_AND_MULTI_ENUM_SIZE_OPERATORS,
  VERSION_OPERATORS,
  WorkflowOperatorKey,
} from 'dashboard/models/workflow';
import DateField from 'design-system/components/DatePicker/DateField';
import Input from 'design-system/components/Input';
import { paragraphMixins } from 'design-system/mixins/text';
import { JsonSchemaAllTypes } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import { JsonSchema } from 'lib/json-schema';
import { assertNever } from 'lib/utils/types';

const generateDefaultSelectOptions = (possibilities: string[]) => {
  if (possibilities == null || possibilities.length === 0) {
    return [];
  }

  return possibilities.map((possibility) => ({
    value: possibility,
    label: possibility,
  }));
};

const SUPPORTED_LIST_TYPES = new Set([
  ApiModel.ListBrowserFingerprint,
  ApiModel.ListDeviceFingerprint,
  ApiModel.ListCountry,
  ApiModel.ListEmailAddress,
  ApiModel.ListField,
  ApiModel.ListIpAddress,
  ApiModel.ListName,
  ApiModel.ListPhoneNumber,
  ApiModel.ListString,
]);

const StyledInput = styled(Input)`
  height: ${({ theme }) => theme.element.height.xs};
  ${paragraphMixins.md};
`;

interface BaseConditionalInputPossibilitiesProps {
  operator?: WorkflowOperatorKey;
  value: JsonLogicValue;
  fieldSchema: JsonSchema;
  isMulti: boolean;
  setFieldValue: (value: JsonLogicValue) => void;
}

const BaseConditionalInputPossibilities: FC<BaseConditionalInputPossibilitiesProps> = ({
  operator,
  value,
  fieldSchema,
  isMulti,
  setFieldValue,
}) => {
  const actionMeta = useWorkflowActionMeta();

  const onChange = (possibility: string | number | boolean) => {
    setFieldValue(possibility);
  };

  const onChangeRange = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(e.target.value);
  };

  const onChangeString = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(e.target.value);
  };

  if (fieldSchema == null) {
    return <StyledInput value={value as string} onChange={onChangeString} />;
  }

  if (operator && [WorkflowOperatorKey.InList, WorkflowOperatorKey.NotInList].includes(operator)) {
    const listOptions = generateListOptions(
      actionMeta.lists.filter(
        (list) =>
          list.attributes.status === ListStatus.Active && SUPPORTED_LIST_TYPES.has(list.type),
      ),
    );

    return (
      <ReactSelect
        id='base-conditional-input-possibilities__list-input'
        aria-label='Select a list'
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        styles={reactSelectStyles}
        value={value as string | string[]}
        isMulti={isMulti}
        onChange={onChange}
        options={listOptions}
        hideSelectedOptions={false}
        portal
      />
    );
  }

  const renderInput = () => {
    const schemaType = getJsonSchemaType(fieldSchema);
    const options = generateConditionalOptions(fieldSchema);

    switch (schemaType) {
      case JsonSchemaAllTypes.Date:
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        if (AGE_OPERATORS.includes(operator)) {
          return (
            <StyledInput
              aria-label='Enter number value'
              type='number'
              min={0}
              value={(value as number) || ''}
              hasError={(value as number) < 0}
              onChange={onChangeRange}
            />
          );
        } else {
          return <DateInput onChange={onChange} value={value} />;
        }
      case JsonSchemaAllTypes.Enum: {
        return (
          <div data-test='base-conditional-input-possibilities__enum-input'>
            <ReactSelect
              id='base-conditional-input-possibilities__enum-input'
              aria-label='Select option'
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              styles={reactSelectStyles}
              value={value as string | string[]}
              isMulti={isMulti}
              onChange={onChange}
              options={options}
              hideSelectedOptions={false}
              portal
            />
          </div>
        );
      }
      case JsonSchemaAllTypes.MultiEnum: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        if (ARRAY_AND_MULTI_ENUM_SIZE_OPERATORS.includes(operator)) {
          return (
            <StyledInput
              aria-label='Enter number value'
              type='number'
              min={0}
              value={(value as number) || ''}
              hasError={(value as number) < 0}
              onChange={onChangeRange}
            />
          );
        } else {
          return (
            <div data-test='base-conditional-input-possibilities__multi-enum-input'>
              <ReactSelect<JsonLogicValue, OptionType<JsonLogicValue>, true>
                id='base-conditional-input-possibilities__multi-enum-input'
                aria-label='Select option'
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                styles={reactSelectStyles}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                value={[].concat(value)}
                isMulti={true}
                onChange={onChange}
                options={options}
                hideSelectedOptions={false}
                portal
              />
            </div>
          );
        }
      }
      case JsonSchemaAllTypes.GroupedEnum: {
        return (
          <ReactSelect
            id='base-conditional-input-possibilities__grouped-enum-input'
            aria-label='Select option'
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            styles={reactSelectStyles}
            value={value as string | string[]}
            isMulti={isMulti}
            onChange={onChange}
            options={options}
            hideSelectedOptions={false}
            portal
          />
        );
      }
      case JsonSchemaAllTypes.Integer:
      case JsonSchemaAllTypes.Number:
        return (
          <StyledInput
            data-test='base-conditional-input-possibilities__number-input'
            aria-label='Enter number value'
            type='number'
            value={(value as number) || ''}
            onChange={onChangeRange}
          />
        );
      case JsonSchemaAllTypes.String:
        if (operator && VERSION_OPERATORS.includes(operator)) {
          return (
            <StyledInput
              aria-label='Enter version number'
              type='text'
              placeholder='e.g. 1.0.0'
              value={(value as string) || ''}
              onChange={onChangeString}
            />
          );
        } else if (isMulti) {
          return (
            <ReactCreatableSelect
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              styles={reactSelectStyles}
              value={compact(castArray(value as string | string[]))}
              isMulti
              onChange={onChange}
              options={generateDefaultSelectOptions(compact(castArray(value as string | string[])))}
              hideSelectedOptions={false}
              menuPlacement='bottom'
              portal
              id='base-conditional-input-possibilities__string-input--multi'
            />
          );
        }
        return (
          <StyledInput
            data-test='base-conditional-input-possibilities__string-input'
            aria-label='Enter text value'
            value={(value as string) || ''}
            onChange={onChangeString}
          />
        );
      case JsonSchemaAllTypes.Boolean: {
        const options: OptionType<boolean>[] = [
          {
            label: 'true',
            value: true,
          },
          {
            label: 'false',
            value: false,
          },
        ];
        return (
          <div data-test='base-conditional-input-possibilities__boolean-input'>
            <ReactSelect
              id='base-conditional-input-possibilities__boolean-input'
              aria-label='Select true/false'
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              styles={reactSelectStyles}
              value={value as any}
              onChange={onChange}
              options={options as any}
              hideSelectedOptions={false}
              portal
            />
          </div>
        );
      }
      case JsonSchemaAllTypes.Array: {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const arrayOptions = generateConditionalOptions(fieldSchema.items);
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const arrayType = getJsonSchemaType(fieldSchema.items);

        if (arrayType === JsonSchemaAllTypes.Boolean) {
          // (evan.qi) Special case for boolean needed to type options as any.
          const booleanOptions = [
            { label: 'true', value: true },
            { label: 'false', value: false },
          ];
          return (
            <ReactSelect
              id='base-conditional-input-possibilities__array-input'
              aria-label='Select true/false'
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              styles={reactSelectStyles}
              value={value as any}
              isMulti
              onChange={onChange}
              options={booleanOptions as any}
              hideSelectedOptions={false}
              portal
            />
          );
        } else if (!arrayOptions.length) {
          return (
            <ReactCreatableSelect
              id='base-conditional-input-possibilities__array-input'
              aria-label='Select/create options'
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              styles={reactSelectStyles}
              value={(value as string[]) ?? []}
              isMulti
              onChange={onChange}
              options={generateDefaultSelectOptions(Array.from(value as string))}
              hideSelectedOptions={false}
              menuPlacement='bottom'
              portal
            />
          );
        } else {
          return (
            <ReactSelect
              id='base-conditional-input-possibilities__array-input'
              aria-label='Select options'
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              styles={reactSelectStyles}
              value={(value as string[]) ?? []}
              isMulti
              onChange={onChange}
              options={arrayOptions}
              hideSelectedOptions={false}
              menuPlacement='bottom'
              portal
            />
          );
        }
      }
      case JsonSchemaAllTypes.File:
      case JsonSchemaAllTypes.Object:
      case JsonSchemaAllTypes.Relation:
      case JsonSchemaAllTypes.Unknown:
        return null;
      default:
        assertNever(schemaType);
    }
  };

  return <div data-test='base-conditional-input-possibilities__container'>{renderInput()}</div>;
};

export default BaseConditionalInputPossibilities;

interface DateInputInterface {
  value: JsonLogicValue;
  onChange: (possibility: string | number) => void;
}

export const DateInput = ({ value, onChange }: DateInputInterface) => {
  const handleDateChange = (date: CalendarDate) => {
    if (date && DateTime.fromISO(date.toString()).isValid) {
      onChange(DateTime.fromISO(date.toString()).toFormat('yyyy-MM-dd'));
    }
  };

  let dateFieldValue;
  try {
    dateFieldValue = parseDate(value?.toString() ?? '');
  } catch (e) {
    dateFieldValue = null;
  }

  return (
    <div
      data-test='base-conditional-input-possibilities__date-input'
      id='base-conditional-input-possibilities__date-input'
    >
      <DateField
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        onChange={handleDateChange}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        value={dateFieldValue}
        id='base-conditional-input-possibilities__date-field'
        aria-label='Input date'
      />
    </div>
  );
};
